import styled from 'styled-components';

export const Grid = styled.div<{ $embedded?: boolean }>`
  padding: ${({ $embedded }) => ($embedded ? '0' : '25px 30px')};
`;

export const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.surface};
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  border-radius: 5px;
  padding: 25px;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 15px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

export const CardTitle = styled.h4`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const PlanBox = styled.div`
  text-align: right;
  white-space: nowrap;
`;

export const PlanLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const PlanValue = styled.div`
  margin-top: 2px;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

export const MeterWrapper = styled.div`
  margin-bottom: 20px;
`;

export const MeterTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 7px;
`;

export const MeterLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const MeterValue = styled.span<{ $state: 'ok' | 'over' | 'under' }>`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $state }) => {
    if ($state === 'ok') return theme.colors.success.success;
    if ($state === 'over') return theme.colors.danger.danger;
    return theme.colors.surface.onSurfaceVariant;
  }};
`;

// Hue única com intervalos de 2px: o trabalho da barra é mostrar se a soma
// fecha 100%, não comparar SPEs entre si.
export const Track = styled.div`
  display: flex;
  gap: 2px;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

export const Segment = styled.div<{ $width: number; $over?: boolean }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background-color: ${({ theme, $over }) =>
    $over ? theme.colors.danger.danger : theme.colors.primary.primary};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Th = styled.th<{ $right?: boolean }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 0 12px 9px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

export const Td = styled.td<{ $right?: boolean }>`
  padding: 13px 12px;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
  vertical-align: middle;
`;

export const SpeName = styled.div`
  font-weight: 600;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

export const SpeDocument = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const WeightField = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const WeightInput = styled.input<{ $invalid?: boolean }>`
  width: 82px;
  padding: 7px 9px;
  text-align: right;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  border-radius: 4px;
  outline: none;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  background-color: ${({ theme }) => theme.colors.surface.surface};
  border: 1.5px solid
    ${({ theme, $invalid }) =>
      $invalid ? theme.colors.danger.danger : theme.colors.surface.surfaceOutline};

  &:read-only {
    background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
    color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
    border-color: transparent;
  }
`;

export const Percent = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const AdjustChip = styled.span`
  display: inline-block;
  margin-left: 7px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.information.information};
  background-color: ${({ theme }) =>
    `color-mix(in srgb, ${theme.colors.information.informationContainer}, ${theme.colors.surface.surface})`};
`;

export const Amount = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

export const TotalRow = styled.tr`
  td {
    border-bottom: none;
    border-top: 2px solid ${({ theme }) => theme.colors.surface.onSurface};
    padding-top: 14px;
    font-weight: 700;
  }
`;

export const TotalLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const TotalValue = styled.span<{ $invalid?: boolean }>`
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $invalid }) =>
    $invalid ? theme.colors.danger.danger : theme.colors.surface.onSurface};
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

export const Message = styled.div<{ $state: 'ok' | 'over' | 'under' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme, $state }) => {
    if ($state === 'ok') return theme.colors.success.success;
    if ($state === 'over') return theme.colors.danger.danger;
    return theme.colors.surface.onSurfaceVariant;
  }};
`;

export const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

// --- Cadastro de SPEs -------------------------------------------------------

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Botões de linha só com ícone. Ficam discretos até o hover para não competir
// com o campo de peso, que é o controle principal da tabela.
export const RowActions = styled.span`
  display: inline-flex;
  gap: 2px;
  justify-content: flex-end;
`;

export const RowAction = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.danger.danger : theme.colors.surface.onSurface};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const EmptyState = styled.div`
  padding: 34px 24px;
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.colors.surface.surfaceOutline};
  border-radius: 6px;
`;

export const EmptyTitle = styled.h5`
  margin: 0 0 6px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

export const EmptyDescription = styled.p`
  max-width: 420px;
  margin: 0 auto 18px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

export const RemoveConfirmText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

export const RemoveConfirmHint = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

// Linha da ciência da matriz nos Termos de Uso, acima da distribuição.
export const TermsLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 4px 0 18px;
`;

export const TermsLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;
